import { z } from 'zod';
import { NullableDateInput, stringifyDate } from '@superdispatch/dates';

const vehiclesPageFilterStatus = [
  'all',
  'released',
  'on_hold',
  'canceled',
  'new',
  'new,released',
] as const;

export const subhauledVehicleFilterStatus = [
  'all',
  'offered',
  'revoked',
  'accepted',
  'delivered',
  'picked_up',
] as const;

export const shipmentStatuses = [
  'ASN',
  'RELEASED',
  'CANCELED',
  'ON_HOLD',
  'OFFERED',
  'OFFER_CANCELED',
  'OFFER_ACCEPTED',
  'OFFER_DECLINED',
  'DELIVERED',
  'PICKED_UP',
] as const;

export const carrierSearchTypes = [
  'name_usdot',
  'email',
  'mc_number',
  'phone_number',
] as const;

function toStringValue(value: unknown): unknown {
  return typeof value === 'number' || typeof value === 'boolean'
    ? String(value)
    : value;
}

function toStringParam(value: unknown): unknown {
  const text = toStringValue(value);

  return typeof text === 'string' && text.trim() ? text.trim() : null;
}

function toFormNumber(value: unknown): unknown {
  if (typeof value !== 'string') {
    return value;
  }

  const parsed = Number(value.trim());

  return value.trim() && Number.isFinite(parsed) ? parsed : value;
}

function toNullableNumber(value: unknown): unknown {
  return value == null || value === '' ? null : Number(value);
}

function toDateTimeISO(value: unknown): unknown {
  return stringifyDate(value as NullableDateInput, { format: 'DateTimeISO' });
}

export type ShipmentPointDTO = z.infer<typeof locationSchema>;
const locationSchema = z.object({
  name: z.preprocess(toStringValue, z.string().catch('')),
  code: z.preprocess(toStringValue, z.string().catch('')),
  address: z.preprocess(toStringValue, z.string().catch('')),
  city: z.preprocess(toStringValue, z.string().catch('')),
  state: z.preprocess(toStringValue, z.string().catch('')),
  zip: z.preprocess(toStringValue, z.string().catch('')),
  latitude: z.preprocess(toNullableNumber, z.number().nullable().catch(null)),
  longtitude: z.preprocess(toNullableNumber, z.number().nullable().catch(null)),
});

const vehicleSchema = z.object({
  make: z.preprocess(toStringValue, z.string().catch('')),
  model: z.preprocess(toStringValue, z.string().catch('')),
  year: z.preprocess(toNullableNumber, z.number().nullable().catch(null)),
  color: z.preprocess(toStringValue, z.string().catch('')),
  type: z.preprocess(toStringValue, z.string().catch('')),
  vin: z.preprocess(toStringValue, z.string().catch('')),
  lot_number: z.preprocess(toStringValue, z.string().catch('')),
  weight_lbs: z.preprocess(toNullableNumber, z.number().nullable().catch(null)),
  wheelbase_in: z.preprocess(
    toNullableNumber,
    z.number().nullable().catch(null),
  ),
});

export type ShipmentDTO = z.infer<typeof shipmentSchema>;
export const shipmentSchema = z.object({
  guid: z.preprocess(toStringValue, z.string().catch('')),
  vinedi_id: z.preprocess(toNullableNumber, z.number().nullable().catch(null)),
  status: z.enum(shipmentStatuses).catch('ASN'),
  created_at: z.preprocess(toStringValue, z.string().catch('')),
  changed_at: z.preprocess(toStringValue, z.string().catch('')),
  customer: z.preprocess(toStringValue, z.string().catch('')),
  vehicle: vehicleSchema,
  origin: locationSchema.extend({
    latest_pickup_at: z.preprocess(
      toDateTimeISO,
      z.string().nullable().catch(null),
    ),
  }),
  destination: locationSchema.extend({
    earliest_delivery_at: z.preprocess(toStringValue, z.string().catch('')),
    latest_delivery_at: z.preprocess(toStringValue, z.string().catch('')),
  }),
  distance_mi: z.preprocess(
    toNullableNumber,
    z.number().nullable().catch(null),
  ),
  price_usd: z.preprocess(toNullableNumber, z.number().nullable().catch(null)),
  price_per_mile_usd: z.preprocess(
    toNullableNumber,
    z.number().nullable().catch(null),
  ),
  load: z
    .object({
      number: z.preprocess(toStringValue, z.string().catch('')),
      guid: z.preprocess(toStringValue, z.string().catch('')),
    })
    .nullable()
    .prefault({ number: '', guid: '' }),
  subhauler: z
    .object({
      name: z.preprocess(toStringValue, z.string().catch('')),
      guid: z.preprocess(toStringValue, z.string().catch('')),
    })
    .nullable()
    .prefault({ name: '', guid: '' }),
});

export type BundledShipementLoadsDTO = z.infer<
  typeof bundledShipementLoadsSchema
>;
export const bundledShipementLoadsSchema = z.object({
  guid: z.string().prefault(''),
  number: z.string().prefault(''),
  customer: z.string().prefault(''),
  vehicle: vehicleSchema,
  driver: z.object({
    name: z.string().nullable().prefault(''),
    email: z.string().nullable().prefault(''),
  }),
  origin: locationSchema.extend({
    scheduled_date: z.string().nullish().prefault(null),
  }),
  destination: locationSchema.extend({
    scheduled_date: z.string().nullish().prefault(null),
  }),
  distance_mi: z.number().nullable().prefault(null),
  price_usd: z.number().nullable().prefault(null),
  price_per_mile_usd: z.number().nullable().prefault(null),
});

export type VehiclesPageFiltersDTO = z.infer<typeof vehiclesPageFiltersSchema>;
export const vehiclesPageFiltersSchema = z.object({
  query: z.preprocess(toStringParam, z.string().nullable().catch(null)),
  vehicle_name: z.preprocess(toStringParam, z.string().nullable().catch(null)),
  status: z
    .enum([...vehiclesPageFilterStatus, ...subhauledVehicleFilterStatus])
    .catch('all'),
  customer: z.preprocess(toStringParam, z.string().nullable().catch(null)),
  origin_city: z.preprocess(toStringParam, z.string().nullable().catch(null)),
  destination_city: z.preprocess(
    toStringParam,
    z.string().nullable().catch(null),
  ),
  origin_state: z.preprocess(toStringParam, z.string().nullable().catch(null)),
  destination_state: z.preprocess(
    toStringParam,
    z.string().nullable().catch(null),
  ),
  origin_name: z.preprocess(toStringParam, z.string().nullable().catch(null)),
  origin_address: z.preprocess(
    toStringParam,
    z.string().nullable().catch(null),
  ),
  origin_zip: z.preprocess(toStringParam, z.string().nullable().catch(null)),
  destination_name: z.preprocess(
    toStringParam,
    z.string().nullable().catch(null),
  ),
  destination_address: z.preprocess(
    toStringParam,
    z.string().nullable().catch(null),
  ),
  destination_zip: z.preprocess(
    toStringParam,
    z.string().nullable().catch(null),
  ),
  ordering: z.preprocess(toStringParam, z.string().nullable().catch(null)),
  sort: z.preprocess(toStringParam, z.string().nullable().catch(null)),
  bundle: z.preprocess(toStringParam, z.string().nullable().catch(null)),
  guid: z.preprocess(toStringParam, z.string().nullable().catch(null)),
  bundle_drawer: z.preprocess(toStringParam, z.string().nullable().catch(null)),
  subhaul_drawer: z.preprocess(
    toStringParam,
    z.string().nullable().catch(null),
  ),
  filter_by_bundle_keys: z.preprocess(
    toStringParam,
    z.string().nullable().catch(null),
  ),
});

export function isValidVehiclesPageFilterStatus(
  value: string,
): value is VehiclesPageFiltersDTO['status'] {
  return (vehiclesPageFilterStatus as readonly string[]).includes(value);
}

export type ShipmentCustomerDTO = z.infer<typeof shipmentCustomerSchema>;
export const shipmentCustomerSchema = z.object({
  customer: z.string().prefault(''),
});

export type ShipmentCityParamsDTO = z.infer<typeof shipmentCityParamsSchema>;
export const shipmentCityParamsSchema = z.object({
  direction: z.enum(['origin', 'destination']).catch('origin'),
  city: z.string().optional(),
});

export type ShipmentCityDTO = z.infer<typeof shipmentCitySchema>;
export const shipmentCitySchema = z.object({
  city: z.string().prefault(''),
  state: z.string().prefault(''),
});

export type ShipmentBuildPreviewParamsDTO = z.infer<
  typeof shipmentBuildPreviewParamsSchema
>;
export const shipmentBuildPreviewParamsSchema = z.object({
  guid: z.string().nullable().prefault(''),
  bundle: z.boolean().nullable().prefault(false),
});

export type ShipmentCarrierParamsDTO = z.infer<
  typeof shipmentCarrierParamsSchema
>;
export const shipmentCarrierParamsSchema = z.object({
  name_usdot: z.string().optional(),
  email: z.string().optional(),
  mc_number: z.string().optional(),
  phone_number: z.string().optional(),
});

export type ShipmentCarrierDTO = z.infer<typeof shipmentCarrierSchema>;
export const shipmentCarrierSchema = z.object({
  guid: z.string().prefault(''),
  name: z.string().prefault(''),
  rating: z.object({
    percentage: z.number().nullable().prefault(null),
    count: z.number().nullable().prefault(null),
  }),
  partnered_loads_count: z.number().nullable().prefault(null),
});

export type ShipmentBuildPreviewDTO = z.infer<
  typeof shipmentBuildPreviewSchema
>;
export const shipmentBuildPreviewSchema = z.object({
  customer: z.string().prefault(''),
  total_weight_lbs: z.number().nullable().prefault(null),
  total_loads: z.number().nullable().prefault(null),
  origin: z.object({
    code: z.string().prefault(''),
    city: z.string().prefault(''),
    state: z.string().prefault(''),
    zip: z.string().prefault(''),
    latest_pickup_at: z.string().nullish().prefault(null),
  }),
  destination: z.object({
    code: z.string().prefault(''),
    city: z.string().prefault(''),
    state: z.string().prefault(''),
    zip: z.string().prefault(''),
    earliest_delivery_at: z.string().nullish().prefault(null),
    latest_delivery_at: z.string().nullish().prefault(null),
  }),
  vehicles: z
    .array(
      z.object({
        make: z.string().prefault(''),
        model: z.string().prefault(''),
        year: z.number().nullable().prefault(null),
        type: z.string().prefault(''),
      }),
    )
    .prefault([]),
});

export type ShipmentSubhaulPreviewDTO = z.infer<
  typeof shipmentSubhaulPreviewSchema
>;
export const shipmentSubhaulPreviewSchema = z.object({
  customer: z.string().prefault(''),
  price: z.number().nullable().prefault(null),
  total_weight_lbs: z.number().nullable().prefault(null),
  origin: z.object({
    code: z.string().prefault(''),
    city: z.string().prefault(''),
    state: z.string().prefault(''),
    zip: z.string().prefault(''),
    latest_pickup_at: z.string().nullish().prefault(null),
  }),
  destination: z.object({
    code: z.string().prefault(''),
    city: z.string().prefault(''),
    state: z.string().prefault(''),
    zip: z.string().prefault(''),
    earliest_delivery_at: z.string().nullish().prefault(null),
    latest_delivery_at: z.string().nullish().prefault(null),
  }),
  vehicles: z
    .array(
      z.object({
        make: z.string().prefault(''),
        model: z.string().prefault(''),
        year: z.number().nullable().prefault(null),
        type: z.string().prefault(''),
        shipment_guid: z.string().prefault(''),
      }),
    )
    .prefault([]),
});

export type VehicleFormDTO = z.infer<typeof vehicleSubhaulSchema>;
export const vehicleSubhaulSchema = z.object({
  carrier_name: z.string().prefault(''),
  carrier_guid: z.string().min(1, 'Select a carrier').prefault(''),
  payment_method: z.string().min(1, 'This field is required').prefault(''),
  payment_terms: z.string().min(1, 'This field is required').prefault(''),
  subhauling_price: z
    .preprocess(
      toFormNumber,
      z
        .number()
        .nullable()
        .refine((value) => value != null, 'This field is required'),
    )
    .prefault(null),
  original_price: z
    .preprocess(
      toFormNumber,
      z
        .number()
        .nullable()
        .refine((value) => value != null, 'This field is required'),
    )
    .prefault(null),
  shipper_guids: z.array(z.string()).prefault([]),
});

export type VehiclesShipmentCountDTO = z.infer<
  typeof vehiclesShipmentCountSchema
>;
export const vehiclesShipmentCountSchema = z.object({
  shipment: z.number().nullable().prefault(0),
  canceled: z.number().nullable().prefault(0),
  archived: z.number().nullable().prefault(0),
});

export type ShipmentsLastUpdatedDTO = z.infer<
  typeof shipmentsLastUpdatedSchema
>;
export const shipmentsLastUpdatedSchema = z.object({
  shipments_updated_at: z.string().nullable().prefault(null),
});

export type SubhauledLoadCountDTO = z.infer<typeof subhauledLoadCountSchema>;
export const subhauledLoadCountSchema = z.object({
  subhauled: z.number().nullable().prefault(0),
});

export type SubhaulLoadDTO = z.infer<typeof subhaulLoadSchema>;
export const subhaulLoadSchema = z.object({
  original_price: z.number().nullable().optional(),
  subhauling_price: z.number().nullable().prefault(null),
  payment_method: z.string().prefault(''),
  payment_terms: z.string().prefault(''),
  carrier_guid: z.string().prefault(''),
});

export type SubhaulDTO = z.infer<typeof subhaulSchema>;
export const subhaulSchema = subhaulLoadSchema.extend({
  shipment_guids: z.array(z.string().prefault('')).prefault([]),
});

export type SubhaulResponseDTO = z.infer<typeof subhaulResponseSchema>;
export const subhaulResponseSchema = z.object({
  load_guid: z.string().prefault(''),
  carrier_name: z.string().prefault(''),
});

export type SubhauledVehicleDTO = z.infer<typeof subhauledVehicleSchema>;
const subhauledVehicleSchema = z.object({
  can_be_subhauled: z.boolean().nullable().prefault(false),
  can_be_edited: z.boolean().nullable().prefault(false),
  can_be_unbuilt: z.boolean().nullable().prefault(false),
  can_send_bol: z.boolean().nullable().prefault(false),
  can_bol_be_viewed: z.boolean().nullable().prefault(false),
  can_invoice_be_sent: z.boolean().nullable().prefault(false),
  can_be_revoked: z.boolean().nullable().prefault(false),
  can_offer_be_sent: z.boolean().nullable().prefault(false),
  can_online_bol_be_viewed: z.boolean().nullable().prefault(false),
  can_be_marked_as_paid_by_customer: z.boolean().nullable().prefault(false),
  can_be_marked_as_unpaid_by_customer: z.boolean().nullable().prefault(false),
  can_be_marked_as_paid_to_subhauler: z.boolean().nullable().prefault(false),
  can_be_marked_as_unpaid_to_subhauler: z.boolean().nullable().prefault(false),
  guid: z.string().prefault(''),
  status: z
    .enum(['offered', 'revoked', 'accepted', 'delivered', 'picked_up'])
    .catch('offered'),
  load_id: z.string().prefault(''),
  subhauler: z.object({
    name: z.string().prefault(''),
    guid: z.string().prefault(''),
  }),
  customer: z.object({
    name: z.string().prefault(''),
  }),
  vehicles: z.array(vehicleSchema).prefault([]),
  pickup: locationSchema.extend({
    pickup_at: z.string().nullish().prefault(null),
  }),
  delivery: locationSchema.extend({
    delivery_at: z.string().nullish().prefault(null),
  }),
  subhauler_payment: z.object({
    price: z.number().nullable().prefault(null),
    payment_method: z.string().prefault(''),
    payment_terms: z.string().prefault(''),
    status: z.string().prefault(''),
  }),
  customer_payment: z.object({
    status: z.string().prefault(''),
  }),
});

export const ediSetupStatuses = [
  'not_submitted',
  'pending',
  'completed',
] as const;

const ediString = z
  .string()
  .nullish()
  .transform((value) => value ?? '');

const ediBoolean = z.boolean().nullable().prefault(false);

function ediArray<TItem extends z.ZodType>(item: TItem) {
  return z
    .array(item)
    .nullish()
    .transform((value) => value ?? [])
    .prefault([]);
}

export const ediRelationshipStatuses = [
  'moving_spot_loads',
  'under_contract',
  'in_discussions',
] as const;

const optionalScac = ediString.refine(
  (value) => !value || /^[A-Z]{4}$/.test(value),
  'SCAC must be 4 uppercase letters',
);

export type CarrierEDIRelationshipStatus =
  (typeof ediRelationshipStatuses)[number];

export type CarrierEDIPartnerDTO = z.infer<typeof carrierEDIPartnerSchema>;
export const carrierEDIPartnerSchema = z.object({
  guid: ediString,
  oem: ediString,
  display_name: ediString,
  load_number_prefix: ediString,
  expected_vins_per_month: ediString,
  relationship_status: ediString,
  oem_contact: z.object({
    name: ediString,
    email: ediString,
  }),
  has_fenkell_integration: ediBoolean,
  fenkell_username: ediString,
  fenkell_password: ediString,
});

export type CarrierEDIIntegrationDTO = z.infer<
  typeof carrierEDIIntegrationSchema
>;
export const carrierEDIIntegrationSchema = z.object({
  is_edi_enabled: ediBoolean,
  setup_status: z.enum(ediSetupStatuses).catch('not_submitted'),
  scac: optionalScac,
  contact: z.object({
    name: ediString,
    phone: ediString,
    email: ediString,
  }),
  partners: ediArray(carrierEDIPartnerSchema),
});

export type CarrierEDIIntegrationUpdateDTO = Partial<
  Pick<CarrierEDIIntegrationDTO, 'scac' | 'contact'>
>;

export type CarrierEDIPartnerUpdateDTO = Partial<
  Pick<CarrierEDIPartnerDTO, 'display_name' | 'load_number_prefix'>
>;

export interface CarrierEDIPartnerUpdateInputDTO extends CarrierEDIPartnerUpdateDTO {
  guid: string;
}

export type CarrierEDIOptionDTO = z.infer<typeof carrierEDIOptionSchema>;
const carrierEDIOptionSchema = z.object({
  value: ediString,
  label: ediString,
});

export type CarrierEDIOptionsDTO = z.infer<typeof carrierEDIOptionsSchema>;
export const carrierEDIOptionsSchema = z.object({
  oems: ediArray(carrierEDIOptionSchema),
  expected_vins_per_month: ediArray(ediString),
  relationship_statuses: ediArray(ediString),
});

const requiredString = z.string().min(1, 'This field is required').prefault('');
const requiredEmail = requiredString.refine(
  (value) => z.email().safeParse(value).success,
  'Invalid email',
);

export type VehiclesSetupCompanyInfoDTO = z.infer<
  typeof vehiclesSetupCompanyInfoSchema
>;
export const vehiclesSetupCompanyInfoSchema = z.object({
  scac: optionalScac,
  contact: z.object({
    name: requiredString,
    // Format is validated by `SuspendedFormikPhoneField`'s own `validate`
    // prop (via `PhoneService`) — this only guards presence.
    phone: requiredString,
    email: requiredEmail,
  }),
});

export const relationshipStatuses = [
  'moving_spot_loads',
  'under_contract',
  'in_discussions',
] as const;

const requiredOem = z.string().min(1, 'Select the OEM').prefault('');
const optionalEmail = ediString.refine(
  (value) => !value || z.email().safeParse(value).success,
  'Invalid email',
);

export type VehiclesSetupPartnerDTO = z.infer<
  typeof vehiclesSetupPartnerSchema
>;
export const vehiclesSetupPartnerSchema = z
  .object({
    // Client-only React key so removing a card doesn't bleed local UI state
    // (expanded/password-visible) onto the wrong card — stripped before the
    // values are submitted to the backend.
    _key: ediString,
    oem: requiredOem,
    display_name: ediString,
    load_number_prefix: ediString,
    expected_vins_per_month: ediString,
    // No `.catch()` default on purpose — pre-selecting a status would submit
    // it as if the carrier had made an explicit choice.
    relationship_status: z.enum(
      relationshipStatuses,
      'Select the status of the relationship',
    ),
    oem_contact: z.object({
      name: ediString,
      email: optionalEmail,
    }),
    fenkell_username: ediString,
    fenkell_password: ediString,
  })
  .superRefine((partner, ctx) => {
    if (!partner.fenkell_username === !partner.fenkell_password) {
      return;
    }

    ctx.addIssue({
      code: 'custom',
      message: 'This field is required',
      path: [
        partner.fenkell_username ? 'fenkell_password' : 'fenkell_username',
      ],
    });
  });

export type VehiclesSetupConnectionDetailsDTO = z.infer<
  typeof vehiclesSetupConnectionDetailsSchema
>;
export const vehiclesSetupConnectionDetailsSchema = z.object({
  partners: z
    .array(vehiclesSetupPartnerSchema)
    .min(1, 'Add at least one OEM partner')
    .superRefine((partners, ctx) => {
      const oems = partners.map((partner) => partner.oem).filter(Boolean);

      if (new Set(oems).size !== oems.length) {
        ctx.addIssue({
          code: 'custom',
          message: 'Each OEM partner can only be added once',
        });
      }
    }),
});

export interface SubmitCarrierEDIIntegrationPartnerDTO {
  oem: string;
  relationship_status: string;
  display_name?: string;
  load_number_prefix?: string;
  expected_vins_per_month?: string;
  oem_contact?: { name?: string; email?: string };
  epod_providers?: Array<{ name: string; username: string; password: string }>;
}

export interface SubmitCarrierEDIIntegrationDTO {
  scac?: string;
  contact: { name: string; phone: string; email: string };
  partners: SubmitCarrierEDIIntegrationPartnerDTO[];
}
